import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, TreeDeciduous } from "lucide-react";
import { AUTH_SIGNUP_URL, CATEGORIES, FAMILY_TREES } from "@/lib/data";
import { MFT } from "@/components/site/BrandLinks";
import FamilyTreesArticle from "@/components/site/FamilyTreesArticle";

const ease = [0.22, 1, 0.36, 1];

export default function FamilyTrees() {
  return (
    <main data-testid="family-trees-page" className="pt-32 md:pt-40">
      {/* Masthead */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-10 md:pb-14">
        <div className="flex items-center gap-3 text-[13px] mb-8">
          <Link to="/" className="link-underline text-[color:var(--ink-soft)]">
            Home
          </Link>
          <span className="text-[color:var(--ink-soft)]">/</span>
          <span className="numeric-index">Family Trees Directory</span>
        </div>

        <div className="overline text-[color:var(--rust)] mb-4">§ Family Trees</div>
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, ease }}
          className="font-serif-display leading-[0.9] tracking-[-0.03em] max-w-6xl"
          style={{ fontSize: "clamp(48px, 8.6vw, 160px)" }}
        >
          Currently in{" "}
          <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
            the archive.
          </span>
        </motion.h1>
        <div className="mt-8 grid grid-cols-12 gap-6 md:gap-10 items-end">
          <div className="col-span-12 md:col-span-7">
            <p className="text-[15px] md:text-[17px] leading-[1.6] text-[color:var(--ink-soft)] max-w-2xl">
              A living directory of family trees being built and enriched by
              families across the world. Each tree grows as relatives contribute
              photographs, documents, memories and missing branches.
            </p>
          </div>
          <div className="col-span-12 md:col-span-5 md:text-right">
            <div className="numeric-index text-[color:var(--ink-soft)]">
              {FAMILY_TREES.length} trees on display · 12,480 lineages archived
            </div>
          </div>
        </div>
      </section>

      {/* Long-form editorial article — inserted above the directory */}
      <FamilyTreesArticle />

      {/* Ledger of trees */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-24 pt-6 md:pt-8">
        <div className="mb-6 md:mb-8">
          <div className="overline text-[color:var(--rust)] mb-2">§ The Living Directory</div>
          <h2 className="heritage-title" style={{ fontSize: "clamp(28px, 3.4vw, 46px)" }}>
            Families in the archive.
          </h2>
          <p className="mt-3 max-w-xl text-[15px] text-[color:var(--ink-soft)]">
            A growing directory of family archives from every continent. Every
            row is a chronicle in progress.
          </p>
        </div>
        <div className="rule-strong" />
        {FAMILY_TREES.map((t, i) => (
          <motion.div
            key={t.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: (i % 6) * 0.04, ease }}
            className="grid grid-cols-12 gap-3 md:gap-6 py-6 md:py-8 border-b border-[color:var(--ink-line)] items-baseline group hover:pl-2"
            style={{ transition: "padding-left 500ms cubic-bezier(0.7,0,0.3,1)" }}
            data-testid={`tree-row-${t.id}`}
          >
            <div className="col-span-2 md:col-span-1 numeric-index text-[color:var(--ink-soft)]">
              {String(i + 1).padStart(3, "0")}
            </div>
            <div className="col-span-10 md:col-span-5">
              <h3
                className="font-serif-display leading-[1.05] group-hover:text-[color:var(--rust)]"
                style={{ fontSize: "clamp(20px, 2.2vw, 34px)", transition: "color 400ms ease" }}
              >
                {t.name}
              </h3>
              <div className="mt-2 flex items-center gap-2 text-[13px] text-[color:var(--ink-soft)]">
                <TreeDeciduous size={13} />
                <span>{t.lineage}</span>
              </div>
              <div className="mt-1 numeric-index text-[color:var(--ink-soft)]">{t.region}</div>
            </div>
            <div className="col-span-6 md:col-span-2 numeric-index text-[color:var(--ink-soft)]">
              <div>Est. {t.years}</div>
              <div className="mt-1">{t.generations} generations</div>
            </div>
            <div className="col-span-4 md:col-span-2 numeric-index text-[color:var(--ink-soft)]">
              <div>{t.members} members</div>
              <div className="mt-1">{t.documents} documents</div>
            </div>
            <div className="col-span-2 md:col-span-2 flex justify-end items-baseline">
              <Link
                to="/category/family-tree"
                className="numeric-index text-[color:var(--ink)] group-hover:text-[color:var(--rust)] flex items-center gap-2"
                data-testid={`tree-view-${t.id}`}
              >
                <span className="hidden md:inline">View tree</span>
                <ArrowUpRight
                  size={20}
                  className="transition-transform duration-500 group-hover:rotate-45"
                />
              </Link>
            </div>
          </motion.div>
        ))}
      </section>

      {/* Contribute CTA */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-24">
        <div className="border border-[color:var(--ink)] p-8 md:p-14 grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-8">
            <div className="overline text-[color:var(--ink-soft)] mb-3">§ Start your tree</div>
            <h3
              className="font-serif-display leading-[0.98]"
              style={{ fontSize: "clamp(28px, 3.6vw, 54px)" }}
            >
              Don't see your family yet?{" "}
              <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
                Plant the first branch.
              </span>
            </h3>
            <p className="mt-4 text-[15px] leading-[1.6] text-[color:var(--ink-soft)] max-w-xl">
              Every tree begins with a single name. Start yours today and invite
              relatives to contribute across generations.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right space-y-3">
            <a href={AUTH_SIGNUP_URL} className="btn-rust" data-testid="ft-cta-begin">
              Begin a family tree <span aria-hidden>→</span>
            </a>
            <div className="hidden md:block">
              <Link
                to="/category/genealogy"
                className="link-underline text-[13px]"
                data-testid="ft-cta-genealogy"
              >
                Read the Genealogy guide →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related wings */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-32">
        <div className="overline text-[color:var(--ink-soft)] mb-6">§ Related wings</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-l border-t border-[color:var(--ink-line)]">
          {["heritage", "genealogy", "memorials"].map((slug) => {
            const c = CATEGORIES.find((x) => x.slug === slug);
            if (!c) return null;
            const href =
              slug === "heritage" || slug === "genealogy" || slug === "memorials"
                ? `/category/${slug}`
                : `/category/${slug}`;
            return (
              <Link
                key={slug}
                to={href}
                className="border-r border-b border-[color:var(--ink-line)] p-6 md:p-8 hover:bg-[color:var(--bone-2)] group"
                style={{ transition: "background-color 400ms ease" }}
                data-testid={`ft-related-${slug}`}
              >
                <div className="numeric-index text-[color:var(--ink-soft)]">{c.kicker}</div>
                <h4
                  className="font-serif-display mt-2"
                  style={{ fontSize: "clamp(24px, 2.4vw, 34px)" }}
                >
                  {c.name}
                </h4>
                <div className="mt-8 numeric-index text-[color:var(--ink-soft)] group-hover:text-[color:var(--rust)]">
                  Enter →
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </main>
  );
}
